import { Box, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import RemovableEntry from "../../components/professional/RemovableEntry/RemovableEntry";
import { ProfessionalAddress } from "../../services/professionals";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

/**
 * One of the professional's addresses, as it reads on the professional data
 * card: the kind of place, then the address itself over as many lines as it
 * takes.
 */

/**
 * The workplace kinds the API uses, matching `institutions()` in the user SDK.
 * Narrowed to a union so the translation key is checked rather than built from
 * whatever the payload happens to carry.
 */
const ADDRESS_TYPES = [1, 2, 3, 4, 5, 6] as const;
type AddressType = typeof ADDRESS_TYPES[number];

const asAddressType = (value: unknown): AddressType | undefined =>
  ADDRESS_TYPES.find(code => code === value);

/** Country codes are resolved by the platform, so no table is shipped for it. */
function countryName(code: string | undefined, language: string) {
  if (!code) return "";
  try {
    return (
      new Intl.DisplayNames([language], { type: "region" }).of(code) ?? code
    );
  } catch {
    return code;
  }
}

const subLabelSx = {
  ...typography.body.lSemibold,
  color: semanticColors.text.secondary,
  mt: "8px"
};

const valueSx = {
  ...typography.body.lSemibold,
  color: semanticColors.text.primary
};

interface Props {
  address: ProfessionalAddress;
  /** Separates this entry from the one above it; omitted on the first. */
  separated: boolean;
  /**
   * The professional always keeps one practice address, so the remove action of
   * the only one left is disabled rather than left to answer 400.
   */
  lastPracticeAddress: boolean;
  onRemove: () => void;
}

export default function AddressEntry({
  address,
  separated,
  lastPracticeAddress,
  onRemove
}: Props) {
  const { t, i18n } = useTranslation();

  const addressType = asAddressType(address?.typeAddress);
  const isPractice = Boolean(address?.professional);

  const lines = [
    address?.name,
    address?.street,
    [address?.postalCode, address?.city].filter(Boolean).join(" - "),
    countryName(address?.country, i18n.language)
  ].filter(Boolean);

  return (
    <Box>
      {/* An address runs to several lines, so without a rule between them a
         list of two or three reads as one block. */}
      {separated && <Divider sx={{ my: 1.5 }} />}
      <RemovableEntry
        removeLabel={t("contactLists.remove")}
        blockedReason={
          isPractice && lastPracticeAddress
            ? t("contactLists.blockedLastAddress")
            : undefined
        }
        onRemove={onRemove}
      >
        <Typography sx={subLabelSx}>
          {addressType
            ? t(`addressType.${addressType}`)
            : t("contactLists.addressType")}
          {!isPractice ? ` · ${t("contactLists.privateAddress")}` : ""}
        </Typography>
        {lines.map((line, index) => (
          <Typography key={index} sx={valueSx}>
            {line}
          </Typography>
        ))}
      </RemovableEntry>
    </Box>
  );
}
